import { dispose, useLoader } from '@react-three/fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { Obstruction } from '~/store/objects/types'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { useEffect, useMemo, useRef } from 'react'
import { modelToUI, vectorModelToUI } from '~/components/DrawingCanvas/util/units'
import { Box3, Group, Vector3 } from 'three'
import { cornerPoints } from '~/components/DrawingCanvas/util/boxDragUtil'

type DetailedObstructionMeshProps = {
  obstruction: Pick<Obstruction, 'obstructionType' | 'positions' | 'height'>
}

export function DetailedObstructionMesh({ obstruction }: DetailedObstructionMeshProps) {
  const gltf = useLoader(
    GLTFLoader,
    new URL(`/src/assets/gltf/obstructions/${obstruction.obstructionType}.glb`, import.meta.url).href,
    loader => {
      loader.setMeshoptDecoder(MeshoptDecoder)
    }
  )
  const scene = useMemo(() => gltf.scene.clone(), [gltf])
  const groupRef = useRef<Group>(null!)

  useEffect(() => {
    const height = modelToUI(obstruction.height)
    const { length, width } = cornerPoints(
      obstruction.positions.map(p => vectorModelToUI(p)),
      height
    )
    const boundingBox = new Box3()
    boundingBox.setFromObject(gltf.scene)
    groupRef.current.scale.copy(new Vector3(width, length, height).divide(boundingBox.getSize(new Vector3())))
  }, [obstruction.height, obstruction.positions, gltf])

  useEffect(() => () => dispose(scene), [scene])

  return <primitive ref={groupRef} object={scene} />
}
